import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ErrorMessages } from '../../constants';
import { User, Account, TreeItem } from '../../models';
import { AuthService, AccountService } from '../../services';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { delay } from '../../helpers/helpers';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
declare var bootstrap: any;

@Component({
  selector: 'app-account-selector',
  standalone: true,
  imports: [FormsModule, CommonModule, NgbDropdownModule],
  templateUrl: './account-selector.component.html',
  styleUrl: './account-selector.component.scss'
})
export class AccountSelectorComponent implements AfterViewInit, OnChanges {
  @Output() accountChanged: EventEmitter<Account> = new EventEmitter<Account>();

  @ViewChild('dropdown') dropdown: ElementRef
  @ViewChild('dropdownTrigger') dropdownButton: ElementRef
  @Input() isFilter: boolean = false;
  @Input() positionOffset: string = '0,0';
  @Input() preSelectedAccountId: number | null = null;
  isLoading: boolean = false;
  accountName: string = '';

  // accounts
  user: User;
  accounts: Account[] = [];
  accountsTree: TreeItem[] = [];
  filteredTree: TreeItem[] = [];
  searchTerm: string = '';

  // NOTE: This needs to be an array of objects so 
  // it's easier to format selected accounts
  selectedAccount?: TreeItem;

  constructor(
    private authService: AuthService,
    private accountService: AccountService,
    private toastr: ToastrService) {
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preSelectedAccountId'] && !changes['preSelectedAccountId'].firstChange) {
      this.applyPreSelection();
    }
  }

  ngAfterViewInit() {
    // Ensure dropdown does not overflow the modal
    new bootstrap.Dropdown(this.dropdownButton.nativeElement, {
      boundary: 'window' // Ensures it stays within the viewport
    });
  }

  async init(): Promise<void> {
    this.user = this.authService.getUser();
    await this.getUserAccounts();
    setTimeout(() => {
      this.updateFilter();
    }, 100)
  }

  openDropdown(event: Event): void {
    event.stopPropagation();

    if (!this.dropdown.nativeElement.className.includes('show')) {
      this.dropdownButton.nativeElement.click();
    }
  }

  async getUserAccounts() {
    if (!!this.user) {
      this.isLoading = true;
      try {
        const response = await this.accountService.getUserAccounts();
        this.accountService.setAccounts(response.data!);
        this.accountsTree = this.createAccountsTree(response.data!);
        this.filteredTree = this.accountsTree;
        this.applyPreSelection();
        this.isLoading = false;
      } catch (error: any) {
        console.error(error);
        const message = ErrorMessages[error?.error?.errorCode] || ErrorMessages['errors/default'];
        this.toastr.error(message);
        this.isLoading = false;
      }
    }
  }

  async onCheckboxChange(event: any, item: TreeItem & { checked?: boolean }): Promise<void> {
    // this prevents the user from unselecting an account
    if (item.id === this.selectedAccount?.id) {
      event.preventDefault();
      return;
    }

    // toggle the check 
    this.toggleCheckBox(item);
    this.updateFilter();

    // Wait 100ms then close the dropdown
    // This is for the user to see what they click before it closes
    await delay(200);
    this.accountName = '';
    this.filteredTree = [...this.accountsTree];
    this.dropdownButton.nativeElement.click();
  }

  createAccountsTree(accounts: Account[]): TreeItem[] {
    const accountMap = new Map<number, TreeItem>();
    const roots: TreeItem[] = [];

    accounts.forEach(account => {
      const treeItem: TreeItem = { id: account.id, name: account.name, checked: false, hierarchyLevel: account.hierarchyLevel, children: [] };
      this.applyToggle(treeItem);
      accountMap.set(account.id, treeItem);

      if (account.parentId === 0 || account.hierarchyLevel === 0) {
        roots.push(treeItem);
      } else {
        const parent = accountMap.get(account.parentId);
        if (parent) {
          parent.children.push(treeItem);
        }
      }
    });

    return roots;
  }

  searchAccounts(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (searchTerm) {
      this.filteredTree = this.searchTreeItems(this.accountsTree, searchTerm);
    } else {
      this.filteredTree = this.accountsTree;
    }

    // apply toggle
    this.filteredTree?.forEach(item => {
      this.applyToggle(item);
    });
  }

  searchTreeItems(items: TreeItem[], searchTerm: string): TreeItem[] {
    return items.reduce((acc: TreeItem[], item) => {
      const matchedChildren = this.searchTreeItems(item.children, searchTerm);

      if (item.name.toLowerCase().includes(searchTerm) || matchedChildren.length > 0) {
        acc.push({
          ...item,
          children: matchedChildren
        });
      }

      return acc;
    }, []);
  }

  updateFilter(): void {
    this.accountChanged.next(this.selectedAccount! as unknown as Account);
  }

  clearSelection(): void {
    this.selectedAccount = undefined;
    this.accountName = '';
    this.filteredTree = [...this.accountsTree];
    this.filteredTree.forEach(item => {
      this.toggleChildren(item);
    });
  }

  private applyToggle(item: TreeItem): void {
    item.checked = item.id === this.selectedAccount?.id;
  }

  private toggleCheckBox(item: TreeItem) {
    this.selectedAccount = this.selectedAccount?.id !== item.id ? item : undefined;
    item.checked = this.selectedAccount?.id !== item.id;
    this.toggleOtherAccount();
  }

  private toggleOtherAccount(): void {
    this.filteredTree.forEach(x => {
      x.checked = x.checked = this.selectedAccount?.id !== x.id;
      this.toggleChildren(x);
    });
  }

  private toggleChildren(item: TreeItem) {
    item.checked = false
    item.children.forEach(element => {
      this.toggleChildren(element);
    });
  }

  private applyPreSelection(): void {
    if (this.preSelectedAccountId == null || this.accountsTree.length === 0) return;

    const match = this.findTreeItemById(this.accountsTree, this.preSelectedAccountId);
    if (match) {
      this.selectedAccount = match;
      this.filteredTree.forEach(item => this.applyToggle(item));
      this.updateFilter();
    }
  }

  private findTreeItemById(items: TreeItem[], id: number): TreeItem | null {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children?.length) {
        const found = this.findTreeItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }
}